'use client'

import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useTheme } from './ThemeProvider'

const THEME_OPTIONS = [
  { key: 'themeA', label: '手繪日記' },
  { key: 'themeB', label: '深夜藝廊' },
  { key: 'themeC', label: '黑白簡約' },
  { key: 'themeD', label: '墨金典藏' },
  { key: 'themeE', label: '玫瑰暮光' },
  { key: 'themeF', label: '深海翡翠' },
  { key: 'themeG', label: '紫夜星河' },
  { key: 'themeH', label: '象牙沙龍' },
]

export default function ThemeSwitcher({ lockedThemeKey = null }) {
  const pathname = usePathname()
  const { themeKey, switchTheme, themes, previewMode } = useTheme()
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  const current = THEME_OPTIONS.find((t) => t.key === themeKey)
  const hidden = lockedThemeKey != null || previewMode || pathname.startsWith('/admin')

  useEffect(() => {
    if (!open) return
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('touchstart', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('touchstart', handleClick)
    }
  }, [open])

  if (hidden) return null

  return (
    <div ref={containerRef} className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50">
      {open && (
        <div
          className="absolute bottom-12 right-0 py-1.5 mb-1 flex flex-col gap-1 px-1.5"
          style={{
            minWidth: '130px',
            backgroundColor: 'var(--color-cardBg)',
            border: '1px solid var(--color-cardBorder)',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}
        >
          {THEME_OPTIONS.map((opt) => {
            const t = themes[opt.key]
            if (!t) return null
            const isActive = themeKey === opt.key
            return (
              <button
                key={opt.key}
                onClick={() => { switchTheme(opt.key); setOpen(false) }}
                className="block w-full px-3 py-2 text-left text-xs tracking-wide min-h-[44px] flex items-center transition-transform active:scale-95"
                style={{
                  color: t.colors.text,
                  backgroundColor: t.colors.bg,
                  border: isActive ? `2px solid ${t.colors.accent}` : `1px solid ${t.colors.cardBorder}`,
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontFamily: 'var(--font-body)',
                  cursor: 'pointer',
                }}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                  style={{ backgroundColor: t.colors.accent }}
                />
                {opt.label}
              </button>
            )
          })}
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 h-10 md:h-9 px-3 text-xs tracking-wide transition-all active:scale-95"
        style={{
          backgroundColor: 'var(--color-cardBg)',
          color: 'var(--color-textSecondary)',
          border: '1px solid var(--color-cardBorder)',
          borderRadius: '8px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
        {current?.label}
      </button>
    </div>
  )
}
